<template>
  <div class="flex flex-col">
    <div class="mb-2 flex max-lg:flex-col md:mb-8">
      <div class="flex font-roboto max-lg:flex-col lg:items-center">
        <span class="mr-4 text-[22px] font-medium leading-6 lg:text-[32px]">
          Исторический календарь
        </span>
        <span
          class="text-[20px] font-semibold leading-8 text-specialprojectsection lg:text-[26px]"
        >
          {{ parsedSelectedDates }}
        </span>
      </div>
      <div v-if="withCalendar">
        <BaseTheDatePicker
          :model-value="selectedDates"
          @update:model-value="handleSetDateOnDatePicker"
        >
          <BaseTheButtonNew
            class="uppercase max-md:mt-4 max-md:w-fit lg:ml-5"
            icon="teacher/date"
            icon-position="left"
            text="Выбрать дату"
            type="inverse--gray-border"
            is-small
          />
        </BaseTheDatePicker>
      </div>
      <div class="ml-auto w-fit max-md:hidden">
        <slot name="actions"></slot>
      </div>
    </div>
    <ClientOnly>
      <div class="mb-5 grid grid-flow-col items-end gap-2">
        <div class="grid h-13 w-8 place-content-center">
          <SlidersSliderControl
            type="prev"
            :class="[isBeginning ? 'opacity-50' : 'cursor-pointer']"
            @click="slidePrev()"
          />
        </div>
        <div class="grid select-none grid-flow-col gap-2">
          <Swiper
            :loop="true"
            :slides-per-view="'auto'"
            :initial-slide="initialSlide"
            :centered-slides="true"
            :centered-slides-bounds="true"
            class="swiper-line-dates w-full"
            @swiper="onSwiper"
            @slide-change="hideAllPoppers()"
          >
            <SwiperSlide v-for="dateStr in formattedDates" :key="dateStr">
              <div class="mx-2 flex h-full flex-col">
                <div
                  v-if="isFirstDayOfMonth(dateStr)"
                  class="mb-0.5 text-[12px] font-medium capitalize leading-[14px] text-specialprojectsection"
                >
                  {{ parseMonth(dateStr) }}
                </div>
                <DatesTooltip
                  :disabled="
                    (selectedDates.length === 1 &&
                      formatDate(selectedDates[0]) !== dateStr) ||
                    inInterval(parseDateString(dateStr))
                  "
                  class="mt-auto"
                >
                  <div
                    :ref="(el) => (dateRefs[dateStr] = el)"
                    class="date-item grid size-13 cursor-pointer place-content-center rounded-full font-roboto text-[20px] leading-[15px] transition-[background-color] duration-300 hover:bg-specialprojectsection/10"
                    :class="{ selected: inInterval(parseDateString(dateStr)) }"
                    @click="handleSelectDates(dateStr)"
                  >
                    {{ parseDay(dateStr) }}
                  </div>
                </DatesTooltip>
              </div>
            </SwiperSlide>
          </Swiper>
        </div>
        <div class="grid h-13 w-8 place-content-center">
          <SlidersSliderControl
            type="next"
            :class="[isEnd ? 'opacity-50' : 'cursor-pointer']"
            @click="slideNext()"
          />
        </div>
      </div>
    </ClientOnly>
    <slot name="content"></slot>
    <div class="mt-4 w-fit md:hidden">
      <slot name="actions"></slot>
    </div>
  </div>
</template>

<script setup>
const MAX_RANGE_DAYS = 14; // максимальное количество дат в выборке

import { hideAllPoppers } from 'floating-vue';
import { Swiper, SwiperSlide } from 'swiper/vue';

import { BaseTheDatePicker } from '#components';

defineProps({
  withCalendar: {
    type: Boolean,
    default: false,
  },
});

const route = useRoute();
const router = useRouter();
const toast = useToast();
const {
  onSwiper,
  slideNext,
  slidePrev,
  isBeginning,
  isEnd,
  slideToLoop,
  // swiper: swiperRef,
} = useSwiper();

const scrollableIndex = ref(-1);

// Преобразуем строку в Date объект
const parseDateString = (dateStr) => {
  const [day, month] = dateStr.split('.');
  const year = new Date().getFullYear();
  return new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`); // Добавляем padding
};

// Преобразуем 10.10.2023 в день 10
const parseDay = (dateStr) => {
  const date = parseDateString(dateStr);
  return date.getDate();
};

const parseMonth = (dateStr) => {
  return parseDateString(dateStr).toLocaleDateString('ru-RU', {
    month: 'long',
  });
};

const isFirstDayOfMonth = (dateStr) => {
  return parseDateString(dateStr).getDate() === 1;
};

const getInitialInterval = () => {
  if (!route.query.days) {
    return {
      start: new Date(),
      end: new Date(),
    };
  }
  const dateStr = route.query.days;
  if (dateStr.includes('-')) {
    const dates = dateStr.split('-');
    return {
      start: parseDateString(dates[0]),
      end: parseDateString(dates[1]),
    };
  }
  return {
    start: parseDateString(dateStr),
    end: parseDateString(dateStr),
  };
};

const interval = ref(getInitialInterval());
const dateRefs = reactive({});

// Преобразуем строку в дату для вывода пользователю
const parsedSelectedDates = computed(() => {
  if (!route.query.days) {
    const today = new Date();
    return today.toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long',
    });
  }
  return route.query.days
    ?.split('-')
    ?.map((date) =>
      parseDateString(date).toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: 'long',
      }),
    )
    .filter((date, index, array) => array.indexOf(date) === index)
    .join(' - ');
});

// Массив выбранных дат
const selectedDates = computed(() =>
  [interval.value.start, interval.value.end]
    .filter(Boolean)
    .sort((a, b) => a - b),
);

const inInterval = (date) => {
  const [start, end] = selectedDates.value;
  if (!start || !end) return false;
  return date >= start && date <= end;
};

// Добавляем даты
const addDays = (startDate, daysToAdd) => {
  const date = new Date(startDate);
  date.setDate(date.getDate() + daysToAdd);
  return date;
};

// Обработка клика по дате ИЛИ установка дат при перезагрузке страницы
const handleSelectDates = (clickedDateStr, initialNoQuery = false) => {
  const clickedDate = parseDateString(clickedDateStr);

  scrollableIndex.value = datesArray.findIndex(
    (date) => toShortDate(date) === clickedDateStr,
  );

  if (scrollableIndex.value !== -1) {
    slideToLoop(scrollableIndex.value, 500);
  }

  if (!interval.value.start || interval.value.end) {
    clearSelection();
    interval.value.start = clickedDate;
    if (dateRefs[clickedDateStr]) {
      dateRefs[clickedDateStr].classList.add('selected');
    }
  } else {
    const MIN_DATE = addDays(interval.value.start, -(MAX_RANGE_DAYS - 1));
    const MAX_DATE = addDays(interval.value.start, MAX_RANGE_DAYS - 1);

    if (clickedDate < MIN_DATE || clickedDate > MAX_DATE) {
      toast.info(`Максимальный период ${MAX_RANGE_DAYS} дней`, {
        timeout: 4000,
      });
      interval.value.end = clickedDate < MIN_DATE ? MIN_DATE : MAX_DATE;
    } else {
      interval.value.end = clickedDate;
    }

    const allDates = getFlatDatesArray();
    const start = Math.min(interval.value.start, interval.value.end);
    const end = Math.max(interval.value.start, interval.value.end);

    allDates.forEach((dateString) => {
      const date = new Date(dateString);

      if (date >= start && date <= end) {
        const dateStr = dateString.toLocaleDateString('ru-RU', {
          day: 'numeric',
          month: '2-digit',
          year: 'numeric',
        });
        if (dateRefs[dateStr]) {
          dateRefs[dateStr].classList.add('selected');
        }
      }
    });

    if (!initialNoQuery) {
      setQueryDates(selectedDates.value);
    }
  }
};

// Функция для создания массива дат
const getDatesArray = (start, end) => {
  const arr = [];
  const current = new Date(start);
  while (current <= end) {
    arr.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }
  return arr;
};

// Функция для сброса выбора
const clearSelection = () => {
  Object.values(dateRefs).forEach((item) => {
    item.classList.remove('selected');
  });
  interval.value.start = null;
  interval.value.end = null;
};

// // Получаем текущую дату
// const currentDate = new Date();

// Создаем даты для диапазона
const firstDayOfYear = new Date(new Date().getFullYear(), 0, 1);
// const startDate = new Date();
// startDate.setMonth(currentDate.getMonth() - 1); // Месяц назад

const lastDayOfYear = new Date(new Date().getFullYear(), 11, 31);
// const endDate = new Date();
// endDate.setMonth(currentDate.getMonth() + 1); // Месяц вперед

// Получаем массив дат
const datesArray = getDatesArray(firstDayOfYear, lastDayOfYear);

const formatDate = (date) => {
  return date.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: '2-digit',
    year: 'numeric',
  });
};

const isDatesEqual = (date1, date2) => {
  return formatDate(date1) === formatDate(date2);
};

const getInitialSlide = () => {
  return datesArray.findIndex((date) => {
    return isDatesEqual(date, interval.value.start);
  });
};

const initialSlide = getInitialSlide();

// Форматируем даты для вывода
const formattedDates = datesArray.map(formatDate);

// Функция для разбиения массива по месяцам
function groupDatesByMonth(dates) {
  const result = {};
  const months = [
    'Январь',
    'Февраль',
    'Март',
    'Апрель',
    'Май',
    'Июнь',
    'Июль',
    'Август',
    'Сентябрь',
    'Октябрь',
    'Ноябрь',
    'Декабрь',
  ];

  dates.forEach((dateString) => {
    const [_day, month, _year] = dateString.split('.');
    const monthIndex = parseInt(month) - 1;

    if (!result[monthIndex]) {
      result[monthIndex] = {
        month: months[monthIndex],
        dates: [],
      };
    }

    result[monthIndex].dates.push(dateString);
  });

  return Object.values(result);
}

// Даты по группам для слайдера на сегодняшний день
const groupedDates = groupDatesByMonth(formattedDates);

// Функция для получения всех дат в плоском массиве
const getFlatDatesArray = () => {
  return groupedDates
    .flatMap(
      (month) => month.dates.map((dateStr) => parseDateString(dateStr)), // Преобразуем строки в Date
    )
    .sort((a, b) => a - b); // Сортируем даты
};

// Пишем даты в query
const setQueryDates = (dates) => {
  const parseDates = dates
    .map((date) =>
      date.toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: '2-digit',
      }),
    )
    .filter((date, index, array) => array.indexOf(date) === index);

  router.replace({
    query: {
      ...route.query,
      days: parseDates.join('-'),
    },
  });
};

const handleSetDateOnDatePicker = (dates) => {
  dates.map((d) =>
    handleSelectDates(
      d.toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: '2-digit',
      }),
    ),
  );
};

// Прокручиваем к выбранному элементу
// const scrollToElementInSlider = (selector, attempt = 0) => {
//   if (attempt > 5) return;

//   try {
//     const target = document.querySelector(selector);
//     if (!target) throw new Error();

//     const containerRect = swiperRef.value.el.getBoundingClientRect();
//     // Отступ слева
//     const scrollGap = containerRect.width / 2;
//     const targetRect = target.getBoundingClientRect();

//     let offset = targetRect.left - (containerRect.left + scrollGap);

//     // Корректировка с учетом текущего скролла
//     offset += swiperRef.value.translate;

//     // Проверка границ
//     const maxOffset = swiperRef.value.maxTranslate();

//     const finalOffset = Math.max(Math.min(-offset, 0), maxOffset);
//     if (isNaN(finalOffset)) throw new Error();

//     swiperRef.value.setTransition(800);
//     swiperRef.value.setTranslate(finalOffset);
//   } catch (_) {
//     setTimeout(() => scrollToElementInSlider(selector, attempt + 1), 200);
//   }
// };

onMounted(() => {
  // Если есть выбранные даты, то устанавливаем их
  if (route.query.days) {
    const dateStr = route.query.days;
    if (dateStr.includes('-')) {
      dateStr.split('-').map(handleSelectDates);
    } else {
      new Array(2).fill(dateStr).map(handleSelectDates);
    }
  } else {
    // Если нет выбранных дат, то устанавливаем сегодняшнюю дату
    new Array(2)
      .fill(
        new Date().toLocaleDateString('ru-RU', {
          day: 'numeric',
          month: '2-digit',
        }),
      )
      .map((d) => handleSelectDates(d, true));
  }
});

// Сбрасываем interval при изменении route.query.days
watch(
  () => route.query.days,
  (newDays) => {
    if (!newDays) {
      // Сбрасываем на сегодня
      const todayStr = new Date().toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
      });
      const today = parseDateString(todayStr);
      interval.value = {
        start: today,
        end: today,
      };
    }
  },
);

// // Прокручиваем к выбранным датам
// const handleAfterInitSwiper = () => {
//   scrollToElementInSlider('.date-item.selected');
// };

const toShortDate = (date) => {
  return date.toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
  });
};
</script>

<style lang="scss" scoped>
.selected {
  @apply bg-specialprojectsection/10;
}

.swiper-slide {
  @apply shrink-0 w-fit;
}
</style>
